import { useState, useEffect, useMemo } from 'react'
import { useMatch, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Page,
  Modal,
  LoadingModal,
  TabsList,
  StatisticsCard,
  Container,
  BlockSpace,
  Alert,
  Icon,
  Info
} from 'components'
import Materials from './components/Materials'
import { useForm, FormProvider } from 'react-hook-form'
import * as DraftOV from 'services/draft'
import {
  formatDate,
  formatServerDate,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  isDateBetween
} from 'commons/utils/date'
import { isObjectEmpty } from 'commons/utils/object'
import { useDispatch, useSelector } from 'store/store'
import { setDefaultParams } from 'store/ProductContext'
import {
  SalesOrderFieldsInterface,
  SimulateDraft,
  RequestMaterialsInterface
} from './interfaces'
import { Factories } from 'services/salesOrder/types'
import * as S from './styles'
import { errorMessages } from 'commons/messages'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { verifyDiscount } from 'commons/utils/string'

import {
  setNumberDraft,
  setMaterialsLimitExceed,
  getItems,
  setDraftBody,
  setSimulateOrder,
  setPersistFormData,
  setProceedDisabled,
  setMaterialNoAvailable,
  getQtdByOrderType,
  setMaterialSearch
} from 'store/SalesOrderContext/salesOrderSlice'
import {
  RequestBodyConfirmDraft,
  RequestBodySimulateDraft
} from 'services/draft/interfaces'
import { MaterialsListInterface } from 'store/SalesOrderContext/types/MaterialItemData'
import { URL_CONST } from 'constants/urls'
import { getUser } from 'commons/utils/user'
import ConfigOrder from './components/ConfigOrder'
import OvsTab from './components/OvsTab'
import {
  setConfirmedNavigation,
  setSalesOrderCurrentTab,
  setSalesOrderInitialTab,
  setShowPrompt,
  updateOrderStep
} from 'store/StructuralContext/structuralSlice'
import EmptyMaterials from './components/EmptyMaterials'
import { haveMaterialAvailable, isProceedDisabled, verifyIfIsClosedBox } from './utils'
import { CustomBoolean, TypesValidations } from 'constants/enums'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import OrderOverviewModal from './components/OrderOverviewModal'
import AnalysisTab from './components/AnalysisTab'
import RejectedItemsTab from './components/RejectedTab'
import { CentersFactories } from 'constants/enums'

export type TagMessageType = 'MTO - PREPARAÇÃO' | 'FORA DE LINHA' | ''

const SalesOrder = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const match = useMatch('/gerenciar/pedido/:orderNumber')
  const { orderNumber } = useParams()
  const nrPediRasc = orderNumber || ''
  const [loading, setIsLoading] = useState<boolean>(true)
  const [changeAllDsct, setChangeAllDsct] = useState(false)
  const [openOrderOverview, setOpenOrderOverview] = useState<boolean>(false)
  const [isLoadingGenerateOV, setIsLoadingGenerateOV] = useState<boolean>(false)

  const {
    records,
    draftBody,
    simulateOrder,
    materialSearch,
    loadingMaterials,
    ovsListData: { OrdensVenda: ovsList }
  } = useSelector((state) => state.salesOrder)
  const { materialUrlSelected } = useSelector(({ products }) => products)
  const { salesOrderInitialTab, salesOrderCurrentTab } = useSelector(
    ({ structure }) => structure
  )

  const openRecords = (records: MaterialsListInterface[]) => {
    return records.filter((record) => record.cd_stat_pedi_rasc_item !== 100)
  }
  const hasOV = () => !!draftBody?.totalizador?.qt_tota_item_soli
  const hasPendingND = useMemo(
    () =>
      hasOV() &&
      !!ovsList.length &&
      ovsList.some(
        (ov) =>
          ov.StatusNumber === '00' && // TODO: Refactor to remove "magic number"
          ov.NegociacaoDiferenciada &&
          !ov.DataJustificativaNegociacao
      ),
    [ovsList]
  )
  const isProcessingND = useMemo(
    () =>
      hasOV() &&
      !!ovsList.length &&
      ovsList.some(
        (ov) =>
          ov.StatusNumber === '30' && // TODO: Refactor to remove "magic number"
          ov.NegociacaoDiferenciada &&
          !ov.DataJustificativaNegociacao
      ),
    [ovsList]
  )
  const hasAnalysisRecords = useMemo(
    () => records.some((record) => record.cd_stat_pedi_rasc_item === 10),
    [records]
  )
  const hasOpenRecords = useMemo(
    () =>
      records.some((record) => [0, 20].includes(record.cd_stat_pedi_rasc_item)) ||
      materialSearch,
    [records]
  )
  const hasRejectedRecords = useMemo(
    () => records.some((record) => record.cd_stat_pedi_rasc_item === 90),
    [records]
  )
  const emptyOrder = useMemo(() => records.length === 0, [records])

  const [factories, setFactories] = useState<Factories>([
    { label: CentersFactories.A022, active: true },
    { label: CentersFactories.A026, active: true },
    { label: CentersFactories.A055, active: true },
    { label: CentersFactories.A133, active: true },
    { label: CentersFactories.A297, active: true }
  ])

  const [modalCloseConfirmation, setModalCloseConfirmation] = useState<{
    open: boolean
    OVSaved: boolean
    loading: boolean
    pageLoading: boolean
    fieldsModified: boolean
  }>({
    open: false,
    OVSaved: true,
    loading: false,
    pageLoading: true,
    fieldsModified: false
  })
  const [modalMaterialsLimit, setModalMaterialsLimit] = useState(false)

  const getOVInfoStorage: string | null = localStorage.getItem('ov_info')
  const ovInfo: { fl_prmt_desd: string } =
    getOVInfoStorage && JSON.parse(getOVInfoStorage)
  const initialToggleRenderStatus = (ovInfo && ovInfo.fl_prmt_desd) || TypesValidations.NO
  const initialFields: SalesOrderFieldsInterface = {
    nr_pedi_rasc: '',
    nm_clie: '',
    nr_cpnj_cpf: '',
    cd_clie: '',
    cd_canl_dstb: '',
    ds_endr_entr: '',
    cd_tipo_orde: '',
    nr_dias_adic: '0',
    cd_cond_pgto: '',
    fl_prmt_desd: initialToggleRenderStatus,
    fl_dsct_matr: TypesValidations.YES,
    cd_clie_entr: '',
    ds_text_item_mto: '',
    ds_grup_clie: '',
    ds_cond_pgto: '',
    nr_pedi_clie: ' ',
    dt_ano_mes_cota: '',
    cd_cent_prio: [''],
    dt_alte: '',
    pc_dsct: 0,
    fl_uplo_plan: TypesValidations.NO,
    cd_empr: '',
    cd_orgn_vend: '',
    cd_seto_atvd: '',
    fl_mto_prep: TypesValidations.NO,
    address: '',
    dt_dsjd_pedi: '',
    ds_text_nota_fisc: '',
    ds_item_pedi_rasc: [],
    fl_matr_fora_linh: TypesValidations.NO
  }
  const validation = yup.object().shape({
    nr_pedi_rasc: yup.string(),
    nm_clie: yup.string(),
    nr_cpnj_cpf: yup.string().nullable(),
    cd_canl_dstb: yup.string(),
    ds_endr_entr: yup.string(),
    cd_tipo_orde: yup
      .string()
      .length(4, errorMessages.invalidField)
      .required(errorMessages.emptyField),
    nr_dias_adic: yup.number().max(99, errorMessages.invalidTerm),
    cd_cond_pgto: yup
      .string()
      .required(errorMessages.emptyField)
      .length(4, errorMessages.invalidField),
    fl_prmt_desd: yup.string(),
    fl_dsct_matr: yup.string(),
    fl_mto_prep: yup.string(),
    cd_clie_entr: yup.string().required(errorMessages.emptyField),
    ds_text_item_mto: yup
      .string()
      .nullable()
      .when('fl_mto_prep', {
        is: (val: string) => val === TypesValidations.YES,
        then: yup.string().required(errorMessages.emptyField)
      }),
    ds_grup_clie: yup.string(),
    ds_cond_pgto: yup.string().required(),
    ds_text_nota_fisc: yup.string().nullable(),
    pc_dsct: yup.mixed(),
    dt_dsjd_pedi: yup
      .string()
      .nullable()
      .test(
        'is-in-month',
        'Data de referência inválida',
        (value, { parent: { dt_ano_mes_cota } }) => {
          const refDate = formatDate(dt_ano_mes_cota, 'YYYY-MM-DD', 'YYYYMM')
          const minDate = getFirstDayOfMonth(refDate, 'YYYY-MM-DD')
          const maxDate = getLastDayOfMonth(refDate, 'YYYY-MM-DD')
          const valueFormatted = formatDate(value, 'YYYY-MM-DD')
          const isBetween = isDateBetween(valueFormatted, minDate, maxDate)

          return isBetween
        }
      )
  })

  const methods = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validation),
    defaultValues: initialFields
  })
  const { setValue, reset, formState, getValues, trigger, setFocus } = methods
  const { isDirty, errors } = formState

  const getSalesOrderStorage = () => {
    const getOvNumberStorage: string | null = localStorage.getItem('ov_info')
    const ovNumberStorage: { ov_number: string } =
      getOvNumberStorage && JSON.parse(getOvNumberStorage)

    if (ovNumberStorage && ovNumberStorage.ov_number.length > 0) {
      const value = ovNumberStorage.ov_number
      dispatch(setNumberDraft(value))
    }
  }

  useEffect(() => {
    if (!isObjectEmpty(errors)) {
      toast.error('Campos em vermelho são obrigatórios!')
      const firstError = Object.keys(errors)[0] as keyof SalesOrderFieldsInterface
      setFocus(firstError)
    }
  }, [errors])

  const handleRequestMaterials = (isValid: boolean) => {
    if (!isValid) return
    setRequestMaterials({ ...requestMaterials, loading: true })
    handleSave().then(() => handleSendRequestMaterials())
  }

  const handleSendRequestMaterials = async () => {
    const dataToSend: RequestBodySimulateDraft = {
      nr_simu: '',
      nr_pedi_rasc: nrPediRasc,
      dt_alte_antr: formatServerDate(),
      cd_usua_alte: getUser(),
      ds_item_pedi_rasc: [],
      cd_matr_anls_pedi_rasc: []
    }
    dispatch(setPersistFormData({ ...getValues() }))
    dispatch(updateOrderStep(1))

    DraftOV.postSimulateDraft(dataToSend).then((data: SimulateDraft) => {
      if (!data) {
        setRequestMaterials({ ...requestMaterials, loading: false })
        return
      }
      dispatch(setConfirmedNavigation(true))
      dispatch(setSimulateOrder(data))
      setRequestMaterials({ ...requestMaterials, loading: false })
      dispatch(setProceedDisabled(isProceedDisabled(data)))
      dispatch(setMaterialNoAvailable(haveMaterialAvailable(data.materiais)))

      if (data.qt_tota_nao_atnd === 0) {
        setOpenOrderOverview(true)
      } else {
        navigate(`${URL_CONST.ORDERS}/${orderNumber}${URL_CONST.ORDERS_SIMULATE}`)
      }

      dispatch(setShowPrompt(false))
    })
  }

  const handleGenerateOVs = async () => {
    if (!simulateOrder) return
    setIsLoadingGenerateOV(true)

    if (draftBody.fl_pedi_antp === TypesValidations.YES) {
      setOpenOrderOverview(false)
      setIsLoadingGenerateOV(false)
      if (match) loadOrder()
      else navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
    } else {
      const params: RequestBodyConfirmDraft = {
        dt_alte_antr: formatServerDate(),
        nr_simu: simulateOrder.nr_simu,
        cd_usua_alte: getUser()
      }

      try {
        await DraftOV.postConfirmDraft(params)
        if (simulateOrder.resumo.vl_tota_orde_gera === 0) {
          toast.info('Nenhum material foi solicitado')
          dispatch(setSalesOrderInitialTab(1))
        } else {
          toast.success('Materiais solicitados com sucesso')
          dispatch(setSalesOrderInitialTab(0))
        }
        if (match) loadOrder()
        else navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
      } catch {
        toast.error('Erro ao solicitar materiais')
      } finally {
        setOpenOrderOverview(false)
        setIsLoadingGenerateOV(false)
      }
    }
  }

  const [requestMaterials, setRequestMaterials] = useState<RequestMaterialsInterface>({
    loading: false,
    handleRequestMaterials
  })

  useEffect(() => {
    return () => {
      dispatch(setSalesOrderInitialTab(-1))
      dispatch(setSalesOrderCurrentTab(-1))
    }
  }, [dispatch])

  useEffect(() => {
    if (modalCloseConfirmation.OVSaved && !modalCloseConfirmation.pageLoading) {
      setModalCloseConfirmation({
        ...modalCloseConfirmation,
        loading: false,
        OVSaved: false,
        fieldsModified: true
      })
    }
  }, [isDirty])

  useEffect(() => {
    if (draftBody?.cd_cent_prio_sele) {
      const newFactories: Factories = factories.map((fact) => {
        if (draftBody.cd_cent_prio_sele.includes(fact.label)) {
          return { label: fact.label, active: true }
        }
        return { label: fact.label, active: false }
      })

      setFactories(newFactories)
      setValue(
        'cd_cent_prio',
        newFactories.filter((fact) => fact.active).map((fact) => fact.label)
      )
    }
  }, [draftBody.cd_cent_prio_sele])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleFieldModified = () => {
    setModalCloseConfirmation({
      ...modalCloseConfirmation,
      fieldsModified: true
    })
  }

  const setItensOrder = () => {
    const ds_item_pedi_rasc = openRecords(records).map(
      (record: MaterialsListInterface) => ({
        nr_item_pedi_rasc: record.nr_item_pedi_rasc,
        cd_matr: record.cd_matr,
        cd_cent_matr: record.cd_cent_matr,
        fl_conv: verifyIfIsClosedBox(record) ? TypesValidations.NO : TypesValidations.YES,
        pc_dsct: verifyDiscount(
          changeAllDsct ? draftBody.pc_dsct.toString() : record.pc_dsct.toString()
        ),
        qt_caix_grad: record.qt_caix_grad,
        fl_caix_grad: record.fl_caix_grad,
        matriz: record.tamanhos.map((size) => ({
          cd_cor: record.cd_cor,
          cd_tamh: size.cd_tamh,
          qt_item: getQtdByOrderType(size, record.fl_caix_grad),
          fl_sele: record.fl_sele,
          fl_excl_item: TypesValidations.NO
        }))
      })
    )
    setValue('ds_item_pedi_rasc', ds_item_pedi_rasc)
  }

  const handleSave = async (event?: React.FormEvent<HTMLFormElement> | undefined) => {
    event && event.preventDefault()
    if (!isObjectEmpty(errors)) {
      toast.error('Campos em vermelho são obrigatórios!')
      return
    }

    const returnApi = await DraftOV.getPaymentTermsById({
      ...getValues(),
      fl_orde_tele_vend: TypesValidations.NO
    })

    if (returnApi && returnApi.ds_cond_pgto) {
      setValue('cd_cond_pgto', returnApi.cd_cond_pgto)
      setValue('ds_cond_pgto', returnApi.ds_cond_pgto)
    }

    setModalCloseConfirmation({
      ...modalCloseConfirmation,
      loading: true,
      fieldsModified: false
    })
    setValue('dt_alte', formatServerDate())

    const clie: Record<string, string> = getValues('cd_clie') as unknown as Record<
      string,
      string
    >

    const data = await DraftOV.postMaintainDraft({
      ...getValues(),
      cd_clie: clie?.cd_clie,
      dt_dsjd_pedi: getValues('dt_ano_mes_cota') ? getValues('dt_dsjd_pedi') : undefined,
      nr_pedi_cpra: '',
      ds_text_item_mto: getValues('ds_text_item_mto') || '',
      ds_item_pedi_rasc: []
    })

    if (!data) return
    await DraftOV.getDraftById({
      nr_pedi_rasc: getValues('nr_pedi_rasc')
    }).then((res) => {
      if (res?.dt_alte) {
        dispatch(
          setDraftBody({
            ...draftBody,
            dt_alte: res.dt_alte,
            cd_cond_pgto: res.cd_cond_pgto,
            ds_cond_pgto: res.ds_cond_pgto
          })
        )
      }
    })

    const hasMaterialOnOv = records.length > 0

    if (hasMaterialOnOv && orderNumber) {
      dispatch(getItems(orderNumber))
    }
    setChangeAllDsct(false)

    toast.success('Pedido salvo com sucesso!')
  }

  const MaintainCondicaoPagamento = async () => {
    const fields = getValues()
    const clie: Record<string, string> = getValues('cd_clie') as unknown as Record<
      string,
      string
    >

    await DraftOV.getPaymentTermsById({
      ...fields,
      fl_orde_tele_vend: TypesValidations.NO
    })
      .then((res) => {
        if (res?.ds_cond_pgto) {
          setValue('cd_cond_pgto', res.cd_cond_pgto)
          setValue('ds_cond_pgto', res.ds_cond_pgto)
          DraftOV.postMaintainDraft({
            ...fields,
            cd_clie: clie.cd_clie,
            nr_pedi_cpra: '',
            ds_item_pedi_rasc: fields.ds_item_pedi_rasc,
            ds_cond_pgto: res.ds_cond_pgto
          })
        }
      })
      .catch((err) => {
        toast.error(err.message)
        console.error(err)
      })
  }
  const goOrder = () => {
    navigate(URL_CONST.ORDERS)
  }

  useEffect(() => {
    setItensOrder()
  }, [JSON.stringify(records)])

  const loadOrder = () => {
    if (orderNumber) {
      setIsLoading(true)
      dispatch(setMaterialSearch(''))
      dispatch(setNumberDraft(orderNumber))
      DraftOV.getDraftById({ nr_pedi_rasc: orderNumber })
        .then((data) => {
          if (!data) {
            goOrder()
            return
          }

          const {
            cd_tipo_orde,
            nr_dias_adic,
            cd_cond_pgto,
            fl_prmt_desd,
            ds_cond_pgto,
            nr_pedi_clie,
            dt_ano_mes_cota,
            dt_dsjd_pedi,
            pc_dsct,
            cd_seto_atvd,
            fl_mto_prep,
            cd_clie_entr,
            nr_cpnj_cpf,
            ds_grup_clie,
            cd_clie,
            cd_empr,
            cd_orgn_vend,
            cd_canl_dstb,
            nr_pedi_rasc,
            cd_equi_vend,
            cd_escr_vend,
            cd_regi_vend
          } = data
          const resetValues = {
            cd_tipo_orde,
            nr_dias_adic,
            cd_cond_pgto,
            fl_prmt_desd,
            ds_cond_pgto: ds_cond_pgto || '',
            nr_pedi_clie,
            dt_ano_mes_cota,
            dt_dsjd_pedi,
            pc_dsct,
            cd_seto_atvd,
            fl_mto_prep,
            cd_clie_entr,
            cd_clie: {
              cd_canl_dstb,
              cd_equi_vend,
              cd_escr_vend,
              cd_regi_vend,
              cd_clie,
              cd_empr,
              cd_orgn_vend,
              cd_seto_atvd
            },
            nr_cnpj_cpf: nr_cpnj_cpf,
            ds_grup_clie,
            nr_pedi_rasc,
            address: ''
          }
          reset({
            ...data,
            ...resetValues
          })
          dispatch(setDraftBody(data))
          dispatch(
            setDefaultParams({
              cd_empr: data.cd_empr,
              cd_orgn_vend: data.cd_orgn_vend,
              cd_canl_dstb: data.cd_canl_dstb,
              cd_seto_atvd: data.cd_seto_atvd,
              cd_escr_vend: data.cd_escr_vend,
              cd_equi_vend: data.cd_equi_vend,
              cd_regi_vend: data.cd_regi_vend,
              cd_tipo_orde: data.cd_tipo_orde,
              fl_matr_fora_linh: data.fl_matr_fora_linh as CustomBoolean,
              fl_mto_prep: data.fl_mto_prep as CustomBoolean,
              nr_pedi_rasc: orderNumber
            })
          )

          dispatch(getItems(orderNumber))

          setModalCloseConfirmation({ ...modalCloseConfirmation, pageLoading: false })
          if (!match) MaintainCondicaoPagamento()
          setIsLoading(false)
        })
        .catch(() => toast.error('Erro ao buscar pedido.'))
    }

    scrollToTop()
    getSalesOrderStorage()
  }

  useEffect(() => {
    loadOrder()
  }, [orderNumber, draftBody.cd_clie])

  const exitModalMaterialsLimit = (data: boolean) => {
    setModalMaterialsLimit(data)
    dispatch(setMaterialsLimitExceed(false))
  }

  const processExitModalMaterialsLimit = () => {
    exitModalMaterialsLimit(false)
    navigate(materialUrlSelected)
  }

  const handleProceedAction = async () => {
    const isValid = await trigger()
    handleRequestMaterials(isValid)
  }

  const renderMaterials = () => (
    <Materials
      hasUnsavedOVConfig={!modalCloseConfirmation.OVSaved}
      user={getUser()}
      hasCustommer={draftBody.fl_mto_prep === TypesValidations.YES}
      requestMaterials={requestMaterials}
      handleRequestMaterials={handleProceedAction}
      setModalMaterialsLimit={setModalMaterialsLimit}
      handleFieldModified={handleFieldModified}
    />
  )

  const getSalesOrderInitialTab = () => {
    if (salesOrderInitialTab > -1) {
      return salesOrderInitialTab
    }
    return hasOV() ? 0 : 1
  }

  const renderManageOrderContent = () => {
    return (
      <TabsList
        initialTab={getSalesOrderInitialTab()}
        controlledTab={salesOrderCurrentTab}
        setControlledTab={(tab: number) => dispatch(setSalesOrderCurrentTab(tab))}
      >
        <TabsList.Tab label="Ordens de Venda" disabled={!hasOV()}>
          <OvsTab />
        </TabsList.Tab>
        {emptyOrder || hasOpenRecords || loadingMaterials ? (
          <TabsList.Tab label="Em aberto">{renderMaterials()}</TabsList.Tab>
        ) : (
          <TabsList.Tab label="Em aberto">
            <EmptyMaterials />
          </TabsList.Tab>
        )}
        <TabsList.Tab label="Em análise" disabled={!hasAnalysisRecords}>
          <AnalysisTab />
        </TabsList.Tab>
        <TabsList.Tab label="Saldo não atendido" disabled={!hasRejectedRecords}>
          <RejectedItemsTab />
        </TabsList.Tab>
      </TabsList>
    )
  }

  const StockAlert = () => (
    <S.AlertWrapper>
      <Alert
        fullWidth
        kind="warning"
        title="Os materiais possuem estoque dinâmico, e para garantir a disponibilidade, é necessário enviar as OVs ao SAP."
      />
    </S.AlertWrapper>
  )

  const NDAlert = () => (
    <S.AlertWrapper>
      <Alert
        fullWidth
        kind="warning"
        title="Suas NDs estão prontas para justificativa."
        message="Envie-as o quanto antes para garantir a disponibilidade dos materiais."
        messageOrientation="right"
      />
    </S.AlertWrapper>
  )

  const ProcessingNDAlert = () => (
    <S.AlertWrapper>
      <Alert
        fullWidth
        kind="warning"
        title="As OVs com ND estão sendo processadas no SAP."
        message=" Você será notificado assim que estiverem disponíveis para justificativa."
        messageOrientation="right"
      />
    </S.AlertWrapper>
  )

  return (
    <FormProvider {...methods}>
      <Page
        fullwidth
        renderHeaderComponent={
          draftBody?.nr_pedi_rasc && (
            <ConfigOrder isButtonRescueData hasOVs={hasOV()} step={0} />
          )
        }
      >
        {loading ? (
          <LoadingModal />
        ) : (
          <S.Container>
            {match ? (
              <>
                <S.StatisticsRow>
                  <StatisticsCard
                    icon={<Icon icon="sandal" color="#94A3BC" size="25" />}
                    title="Volume total pedido"
                    mainValue={numberToDecimalString(
                      draftBody?.totalizador?.qt_tota_pedi_rasc ?? 0
                    )}
                  >
                    <S.InnerRow>
                      <S.Box>
                        <S.BoxRow>
                          <S.BoxTitle>Em aberto</S.BoxTitle>
                          <Info iconSize={10}>
                            Materiais que foram incluídos no pedido, mas que não geraram
                            OVs ou solicitaram análise.
                          </Info>
                        </S.BoxRow>
                        <S.BoxValue>
                          {numberToDecimalString(
                            draftBody?.totalizador?.qt_tota_item_pend ?? 0
                          )}
                        </S.BoxValue>
                      </S.Box>
                      <S.Box>
                        <S.BoxRow>
                          <S.BoxTitle>Faturamento pendente</S.BoxTitle>
                          <Info iconSize={10}>
                            Materiais que geraram OVs, mas estão limitados por bloqueio
                            comercial, bloqueio de crédito, pendencia do usuário ou de
                            aprovação.
                          </Info>
                        </S.BoxRow>
                        <S.BoxValue>
                          {numberToDecimalString(
                            draftBody?.totalizador?.qt_tota_pend_fatm ?? 0
                          )}
                        </S.BoxValue>
                      </S.Box>
                      <S.Box>
                        <S.BoxRow>
                          <S.BoxTitle>Recebido pelo SAP</S.BoxTitle>
                          <Info iconSize={10}>
                            Materiais que estão na fila de produção da fábrica. Esta
                            categoria contém materiais não programados, em produção e em
                            estoque.
                          </Info>
                        </S.BoxRow>
                        <S.BoxValue>
                          {numberToDecimalString(
                            draftBody?.totalizador?.qt_tota_item_sap ?? 0
                          )}
                        </S.BoxValue>
                      </S.Box>
                      <S.Box>
                        <S.BoxRow>
                          <S.BoxTitle>Em remessa</S.BoxTitle>
                          <Info iconSize={10}>
                            Materiais que estão passando pelos últimos preparativos antes
                            do faturamento e entrega ao cliente.
                          </Info>
                        </S.BoxRow>
                        <S.BoxValue>
                          {numberToDecimalString(
                            draftBody?.totalizador?.qt_tota_item_rems ?? 0
                          )}
                        </S.BoxValue>
                      </S.Box>
                      <S.Box>
                        <S.BoxRow>
                          <S.BoxTitle>Faturado</S.BoxTitle>
                          <Info iconSize={10}>
                            Materiais com notas fiscais emitidas e em trânsito ou já
                            entregues aos clientes.
                          </Info>
                        </S.BoxRow>
                        <S.BoxValue>
                          {numberToDecimalString(
                            draftBody?.totalizador?.qt_tota_item_fatm ?? 0
                          )}
                        </S.BoxValue>
                      </S.Box>
                      <S.Box>
                        <S.BoxRow>
                          <S.BoxTitle>Cancelado</S.BoxTitle>
                          <Info iconSize={10}>
                            Volume de materiais que foram cancelados por você, ou pelo
                            sistema, em seu pedido.
                          </Info>
                        </S.BoxRow>
                        <S.BoxValue>
                          {numberToDecimalString(
                            draftBody?.totalizador?.qt_tota_item_canc ?? 0
                          )}
                        </S.BoxValue>
                      </S.Box>
                    </S.InnerRow>
                  </StatisticsCard>
                  <StatisticsCard
                    icon={<Icon icon="money" color="#94A3BC" size="25" />}
                    title="Valor do pedido"
                    titleTooltip="Somatório do valor de todos os materiais do pedido, estando eles em aberto ou alocados em OVs"
                    mainValue={formatToPTBRNumber(
                      draftBody?.totalizador?.vl_tota_item ?? 0,
                      false
                    )}
                  >
                    <S.InnerRow>
                      <S.Box>
                        <S.BoxRow>
                          <S.BoxTitle>Preço médio</S.BoxTitle>
                          <Info iconSize={10}>
                            Valor médio de todos os pares contidos neste pedido, estando
                            eles em aberto ou alocados em OVs
                          </Info>
                        </S.BoxRow>
                        <S.BoxValue>
                          {formatToPTBRNumber(draftBody?.totalizador?.vl_prec_medi ?? 0)}
                        </S.BoxValue>
                      </S.Box>
                    </S.InnerRow>
                  </StatisticsCard>
                </S.StatisticsRow>
                {hasPendingND && <NDAlert />}
                {isProcessingND && <ProcessingNDAlert />}
                {!!hasOpenRecords && <StockAlert />}
                {renderManageOrderContent()}
              </>
            ) : (
              <>
                {!!hasOpenRecords && <StockAlert />}
                <Container fullwidth={true}>
                  <BlockSpace id="material" content={[renderMaterials()]} />
                </Container>
              </>
            )}
          </S.Container>
        )}
        <Modal
          open={modalMaterialsLimit}
          setOpen={exitModalMaterialsLimit}
          title="Limite de itens atingido"
          description="Você só pode adicionar 350 linhas por solicitação, remova materiais da seleção e tente novamente."
          showClose={false}
          buttonText="Ok"
          buttonAction={processExitModalMaterialsLimit}
        />
        <OrderOverviewModal
          open={openOrderOverview}
          setOpen={setOpenOrderOverview}
          onConfirm={handleGenerateOVs}
          loadingConfirm={isLoadingGenerateOV}
        />
      </Page>
    </FormProvider>
  )
}

export default SalesOrder
